/**
 * F3 (UX review) / core F1.3: the ONE place MatchPoint formats money, numbers, percentages and dates. Pages use these
 * (or the components in components/format) instead of their own helpers, so a figure reads the same everywhere.
 *
 * The rules:
 * - en-GB everywhere: `£1,234.56`, `14 Oct 2026`.
 * - Negative numbers use a true minus sign (U+2212) before the symbol: `−£1,234.56`, `−36%`. Display only: CSV
 *   exports and inputs keep plain `-`.
 * - Money has 2 decimals by default; `decimals: 0` for portfolio views (Credit control, Worklist); `compact` for
 *   tiles (`£777.7k`, `£3m`; no trailing .0), with the exact figure in a tooltip.
 * - Zero is the column's choice: `zero: 'amount'` (default) `£0.00`; `'dash'` `—`; `'blank'` ``. Ageing and
 *   allocation columns use a dash, so non-zero figures stand out; balances and totals show £0.00.
 * - Empty (null, undefined, '', not a number) is always `—`.
 * - The currency: GBP and EUR by symbol (`£`, `€`), others by ISO code (`CHF 1,000.00`); `currency: null` gives a
 *   plain number. USD follows `currencyDisplay` (supervisor's rule, F3): `'unambiguous'` (the default) `US$` for
 *   views that mix currencies (group views, multi-currency lists, CSV-like tables); `'local'` `$` where everything
 *   on the view is in one currency (e.g. a US subsidiary's own screens).
 * - Dates: `long` 14 Oct 2026 (the default); `short` 14/10/2026 in dense tables; `month` Oct 2026; `datetime`
 *   14 Oct 2026, 01:25; `day` 14 Oct (chart labels, this year's rows); `time` 01:25; `daytime` 14 Oct, 01:25 (this year's timestamps). Month names are fixed
 *   (Sep, not the browser's "Sept"). A date-only ISO string (2026-10-14) is that calendar day in any timezone.
 */

export const MINUS = '−';
export const DASH = '—';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type Numeric = number | string | null | undefined;
export type ZeroAs = 'amount' | 'dash' | 'blank';

/** The number in a value, or null when there is none (null, '', 'abc', NaN, Infinity). */
export function toNumber(v: Numeric): number | null {
  if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

const SYMBOLS: Record<string, string> = { GBP: '£', EUR: '€', USD: 'US$' };

function grouped(abs: number, decimals: number): string {
  return abs.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function compactBody(abs: number): string {
  const one = (v: number) => grouped(v, 1).replace(/\.0$/, '');      // £16k and £3m, not £16.0k; £777.7k keeps its .7
  if (abs >= 1e9) return `${one(abs / 1e9)}bn`;
  if (abs >= 1e6) return `${one(abs / 1e6)}m`;
  if (abs >= 1e3) return `${one(abs / 1e3)}k`;
  return grouped(abs, 0);
}

export interface MoneyOptions {
  /** 2 (default) or 0. */
  decimals?: number;
  /** Tiles: £777.7k, £3.0m, £1.2bn (under £1,000: whole pounds). */
  compact?: boolean;
  zero?: ZeroAs;
  /** Prefix positive amounts with + (deltas). */
  signed?: boolean;
  /** 'unambiguous' (default): US$ (mixed-currency views). 'local': $ (a view all in one currency). */
  currencyDisplay?: 'local' | 'unambiguous';
}

/** `formatMoney(-1234.5, 'GBP')` → `−£1,234.50`. `currency` null/'' → a plain number with the same rules. */
export function formatMoney(value: Numeric, currency: string | null = 'GBP', opts: MoneyOptions = {}): string {
  const n = toNumber(value);
  if (n === null) return DASH;
  const decimals = opts.decimals ?? 2;
  const rounded = Number(n.toFixed(opts.compact ? 1 : decimals));
  if (rounded === 0 || Object.is(rounded, -0)) {
    if (opts.zero === 'dash') return DASH;
    if (opts.zero === 'blank') return '';
  }
  const abs = Math.abs(n);
  const body = opts.compact ? compactBody(abs) : grouped(abs, decimals);
  const code = (currency || '').toUpperCase();
  const symbol = !code ? '' : code === 'USD' && opts.currencyDisplay === 'local' ? '$' : SYMBOLS[code] ?? `${code} `;
  const sign = n < 0 && rounded !== 0 ? MINUS : opts.signed && n > 0 ? '+' : '';
  return `${sign}${symbol}${body}`;
}

/** Whole or fixed-decimal numbers: `formatNumber(1234.5, 1)` → `1,234.5`; negatives with a true minus. */
export function formatNumber(value: Numeric, decimals = 0, zero: ZeroAs = 'amount'): string {
  return formatMoney(value, null, { decimals, zero });
}

export interface PercentOptions {
  decimals?: number;
  /** 'fraction' (default): 0.36 → 36%. 'percent': 36 → 36%. */
  input?: 'fraction' | 'percent';
  signed?: boolean;
  zero?: ZeroAs;
}

/** `formatPercent(0.364)` → `36%`; `formatPercent(-12.5, { input: 'percent', decimals: 1 })` → `−12.5%`. */
export function formatPercent(value: Numeric, opts: PercentOptions = {}): string {
  const n = toNumber(value);
  if (n === null) return DASH;
  const pct = opts.input === 'percent' ? n : n * 100;
  const s = formatMoney(pct, null, { decimals: opts.decimals ?? 0, signed: opts.signed, zero: opts.zero });
  return s === DASH || s === '' ? s : `${s}%`;
}

export type DateStyle = 'long' | 'short' | 'month' | 'day' | 'datetime' | 'time' | 'daytime';

/** A Date for an ISO date, an ISO timestamp or a Date; a date-only string is local midnight of that day. */
export function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `formatDate('2026-10-14')` → `14 Oct 2026`; `'short'` → `14/10/2026`; empty or invalid → `—`. */
export function formatDate(value: string | Date | null | undefined, style: DateStyle = 'long'): string {
  const d = toDate(value);
  if (!d) return DASH;
  const day = pad(d.getDate()), mon = MONTHS[d.getMonth()], year = d.getFullYear();
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  switch (style) {
    case 'short': return `${day}/${pad(d.getMonth() + 1)}/${year}`;
    case 'month': return `${mon} ${year}`;
    case 'day': return `${day} ${mon}`;
    case 'datetime': return `${day} ${mon} ${year}, ${time}`;
    case 'time': return time;
    case 'daytime': return `${day} ${mon}, ${time}`;
    default: return `${day} ${mon} ${year}`;
  }
}

/** The ISO date (YYYY-MM-DD) of a Date, in local time. */
export function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A date typed in UK order (14/10/2026, 14-10-26, 14 Oct 2026, 2026-10-14) → ISO `2026-10-14`; null when it isn't a
 * real date. Two-digit years are 20xx.
 */
export function parseUkDate(text: string): string | null {
  const t = text.trim();
  if (!t) return null;
  let y: number, mo: number, d: number;
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
  if (m) { y = +m[1]; mo = +m[2]; d = +m[3]; } else if ((m = /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{2}|\d{4})$/.exec(t))) {
    d = +m[1]; mo = +m[2]; y = +m[3] < 100 ? 2000 + +m[3] : +m[3];
  } else if ((m = /^(\d{1,2})\s+([A-Za-z]{3,9})\.?\s+(\d{2}|\d{4})$/.exec(t))) {
    const i = MONTHS.findIndex(x => m![2].toLowerCase().startsWith(x.toLowerCase()));
    if (i < 0) return null;
    d = +m[1]; mo = i + 1; y = +m[3] < 100 ? 2000 + +m[3] : +m[3];
  } else return null;
  const dt = new Date(y, mo - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === mo - 1 && dt.getDate() === d ? isoDate(dt) : null;
}

/**
 * An amount as typed (`18,500`, `£18,500.5`, `(250.00)`, `-1,000`) → a plain decimal string with `decimals` places
 * (`18500.00`, `-250.00`), or null when it isn't a number.
 */
export function parseAmount(text: string, decimals = 2): string | null {
  let t = text.trim().replace(/[£€$,\s]/g, '').replace(/^US/i, '').replace(MINUS, '-');
  if (!t) return null;
  let neg = false;
  const br = /^\((.*)\)$/.exec(t);
  if (br) { neg = true; t = br[1]; }
  if (/CR$/i.test(t)) { neg = true; t = t.replace(/CR$/i, ''); }
  if (!/^[-+]?\d*\.?\d+$/.test(t)) return null;
  const n = Number(t) * (neg ? -1 : 1);
  return Number.isFinite(n) ? n.toFixed(decimals) : null;
}
